import { Box, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import CourseEvaluationAlert from './CourseEvaluationAlert'
import CourseEvaluationHeader from './CourseEvaluationHeader'
import Courses from './Courses'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import LoadingPageV2 from '../../pages/LoadingPageV2'
import PAGE_TITLES from '../../consts/titles'
import { getPagePermissions } from '../../store/slices/roleSlice/selectors'
import { PreparedRouteKeys } from '../../hooks/useAllowedPath'
import { GuidedTourFeature } from '../GuidedTourFeature'
import { useQuery } from '@tanstack/react-query'
import { DistributionManager } from '../../APIManager'
import { SlotStatus } from '../../models/DistributionPlan'
import { WarningHighlightProvider } from '../../components/CustomTable/WarningHighlighterContext'
import ProjectSectionTab from './ProjectSectionTab'
import { SlotTaskProvider, useSlotTaskContext } from '../../contexts/SlotTaskContext'
import BackgroundTaskModal from '../../components/BackgroundTaskModal'
import { notifyError } from '../../utils/notifyError'
import ROUTES from '../../consts/paths'

const SlotTaskBlockedModal: React.FC = () => {
  const { isBlockedModalOpen, closeBlockedModal, taskMessage } = useSlotTaskContext()
  return (
    <BackgroundTaskModal
      isOpen={isBlockedModalOpen}
      onClose={closeBlockedModal}
      taskMessage={taskMessage}
    >
      <Typography variant="body2" fontWeight={600}>While this runs, you cannot:</Typography>
      <Box component="ul" sx={{ mt: 1, pl: 3, mb: 0 }}>
        <Typography component="li" variant="body2">Edit, reset, or sync this project</Typography>
        <Typography component="li" variant="body2">Upload or add course sections</Typography>
        <Typography component="li" variant="body2">Export data</Typography>
      </Box>
    </BackgroundTaskModal>
  )
}

export const ProjectCourseFeature: React.FC = () => {
  const permissions = getPagePermissions()
  const { id } = useParams()
  const navigate = useNavigate()

  const location = useLocation()
  const isFromSetupWizard = location.state?.openModalOnLoad ?? false
  const [guidedTourModal, setGuidedTourModal] = useState<boolean>(isFromSetupWizard)
  const headerRef = useRef<HTMLDivElement>(null)
  const syncButtonRef = useRef<HTMLDivElement>(null)
  const emailSettingsButtonRef = useRef<HTMLDivElement>(null)
  const coursesRef = useRef<HTMLDivElement>(null)

  const { data: slot, isLoading: slotDataLoading, isError: isSlotError, error: slotError } = useQuery({
    queryKey: ['slotDataById', id],
    retry: false,
    queryFn: async () => {
      const response = await DistributionManager.getSlot(id ?? '')
      return response?.data
    },
    enabled: !!id
  })

  useEffect(() => {
    if (isSlotError) {
      notifyError(slotError, { fallbackMessage: 'Failed to load survey project. Please try again.' })
      navigate(ROUTES.DISTRIBUTION_SERIES)
    }
  }, [isSlotError])

  if (slotDataLoading) {
    return <LoadingPageV2 title={PAGE_TITLES.SURVEY_DISTRIBUTION} />
  }

  const status = slot?.data?.slotNodeDetails?.slotStatus ?? 'default'
  const emailEdit = permissions?.includes(PreparedRouteKeys.SlotEmailSettingsPage)

  return (
    <SlotTaskProvider slotId={id ?? ''}>
      <WarningHighlightProvider>
        <GuidedTourFeature
          headerRef={headerRef}
          syncButtonRef={syncButtonRef}
          isGuidedTourOpen={guidedTourModal}
          setIsGuidedTourOpen={setGuidedTourModal}
          emailSettingsButtonRef={emailSettingsButtonRef}
          emailEdit={emailEdit}
          coursesRef={coursesRef}
        />

        <Box display='flex' flexDirection='column' gap={6}>
          <Box ref={headerRef}>
            <CourseEvaluationHeader
              emailSettingsButtonRef={emailSettingsButtonRef}
              setGuidedTourModal={setGuidedTourModal}
              slotData={slot}
              emailEdit={emailEdit} />
          </Box>
          <CourseEvaluationAlert slot={slot?.data} />
          <ProjectSectionTab status={status}/>
          <Box ref={coursesRef}>
            <Courses slot={slot?.data} />
          </Box>
        </Box>

        <SlotTaskBlockedModal />
      </WarningHighlightProvider>
    </SlotTaskProvider>
  )
}
